import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useAuth } from "../hooks/useAuth";
import { LoginCredentials } from "../types/auth";

// --- 1. تعریف استایل‌ها با Styled Components ---

// کانتینر اصلی مشابه کامپوننت Main
const Container = styled.div`
  background-color: #141414;
  color: #ffffff;
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    "Helvetica Neue", Arial, sans-serif;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

// کارت فرم با پس‌زمینه تیره
const FormCard = styled.div`
  background-color: #1f1f1f;
  border: 1px solid #333;
  border-radius: 12px;
  padding: 40px;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
`;

const Title = styled.h2`
  font-size: 2rem;
  font-weight: 600;
  text-align: center;
  margin-bottom: 30px;
  color: #ffffff;
`;

const FormWrapper = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  text-align: right;
`;

const Label = styled.label`
  font-size: 0.9rem;
  color: #b3b3b3;
  margin-bottom: 8px;
`;

const Input = styled.input<{ hasError: boolean }>`
  background-color: #2a2a2a;
  border: 1px solid ${({ hasError }) => (hasError ? "#ff4d4f" : "#444")};
  border-radius: 8px;
  padding: 12px 16px;
  font-size: 1rem;
  color: #ffffff;
  transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;

  &::placeholder {
    color: #666;
  }

  &:focus {
    outline: none;
    border-color: #00d4ff;
    box-shadow: 0 0 0 2px rgba(0, 212, 255, 0.2);
  }

  &:focus-visible {
    outline: 2px solid #00d4ff;
    outline-offset: 2px;
  }
`;

const ErrorMessage = styled.p`
  color: #ff4d4f;
  font-size: 0.8rem;
  margin-top: 6px;
  text-align: right;
`;

const SubmitButton = styled.button<{ disabled: boolean }>`
  background-color: ${({ disabled }) => (disabled ? "#555" : "#00d4ff")};
  color: #141414;
  border: none;
  border-radius: 8px;
  padding: 14px;
  font-size: 1rem;
  font-weight: 600;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  transition: background-color 0.2s ease-in-out, transform 0.2s ease-in-out;
  margin-top: 10px;

  &:hover:not(:disabled) {
    background-color: #00a8cc;
    transform: translateY(-2px);
  }
`;

const LinksContainer = styled.div`
  margin-top: 24px;
  text-align: center;
  font-size: 0.9rem;
  color: #b3b3b3;
`;

const StyledLink = styled(Link)`
  color: #00d4ff;
  text-decoration: none;
  font-weight: 600;
  transition: color 0.2s ease-in-out;

  &:hover {
    color: #00a8cc;
    text-decoration: underline;
  }
`;

// --- 2. کامپوننت اصلی ---

const LoginContainer: React.FC = () => {
  const { login, isLoading } = useAuth();
  const navigate = useNavigate();
  const [apiError, setApiError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginCredentials>();

  const onSubmit = async (data: LoginCredentials) => {
    try {
      setApiError(null);
      await login(data);
      navigate("/dashboard-page");
    } catch (err: any) {
      setApiError(err.response?.data?.message || "ورود با خطا مواجه شد.");
    }
  };

  return (
    <Container>
      <FormCard>
        <Title>ورود به حساب کاربری</Title>
        {apiError && <ErrorMessage>{apiError}</ErrorMessage>}
        <FormWrapper onSubmit={handleSubmit(onSubmit)}>
          <FormGroup>
            <Label htmlFor="username">نام کاربری</Label>
            <Input
              id="username"
              type="text"
              style={{ textAlign: "center" }}
              hasError={!!errors.username}
              placeholder="نام کاربری خود را وارد کنید"
              {...register("username", {
                required: "وارد کردن نام کاربری الزامی است",
              })}
            />
            {errors.username && (
              <ErrorMessage>{errors.username.message}</ErrorMessage>
            )}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="password">رمز عبور</Label>
            <Input
              style={{ textAlign: "center" }}
              id="password"
              type="password"
              hasError={!!errors.password}
              placeholder="رمز عبور خود را وارد کنید"
              {...register("password", {
                required: "وارد کردن رمز عبور الزامی است",
              })}
            />
            {errors.password && (
              <ErrorMessage>{errors.password.message}</ErrorMessage>
            )}
          </FormGroup>

          <SubmitButton type="submit" disabled={isLoading}>
            {isLoading ? "در حال ورود..." : "ورود"}
          </SubmitButton>
        </FormWrapper>

        <LinksContainer>
          <StyledLink to="/reset-password">
            رمز عبور را فراموش کرده‌اید؟
          </StyledLink>
          <p>
            حساب کاربری ندارید؟{" "}
            <StyledLink to="/register">ثبت‌نام کنید</StyledLink>
          </p>
        </LinksContainer>
      </FormCard>
    </Container>
  );
};

export default LoginContainer;
